import { IOTObject } from '../object.model';
import { ParseHealthFromRawSensorTelemetryIA, Sensor } from './sensor';
import { CurrentTelemetry, TypedSensorTelemetry } from './sensor';

import { Notification } from './object';
import { GLOBAL_CONSTANT } from '../../constants/global.constant';
import { groupBy } from '../../utilities/global.util';

const INACTIVE_LEAKAGE_ID = GLOBAL_CONSTANT.INACTIVE_LEAKAGE_ID;
const INACTIVE_STEAM_TRAP_ID = GLOBAL_CONSTANT.INACTIVE_STEAM_TRAP_ID;
const INACTIVE_STEAM_TRAP_ANALYTICS_ID = GLOBAL_CONSTANT.INACTIVE_STEAM_TRAP_ANALYTICS_ID;
const DIRECT_C_LEAKAGE_ID = GLOBAL_CONSTANT.DIRECT_C_LEAKAGE_ID;
const STEAM_TRAP_STATUS_ID = GLOBAL_CONSTANT.STEAM_TRAP_STATUS_ID;

export class Asset implements IOTObject {
  objectID!: number;
  objectName!: string;
  objectTypeID!: number;
  objectType!: string;
  parentID!: number;
  objectStatus!: string;
  colorCode!: string;
  description!: string;
  isAsset!: boolean;
  isConnected!: boolean;
  warning!: boolean;
  critical!: boolean;
  equipmentTypeID?: number;
  equipmentType!: string;
  childCount!: number;
  geoPosition!: string;
  children?: IOTObject[];
  picture!: string;
  thumbnail!: string;
  curLayer!: string;
  nextLayer!: string;
  layerName!: string;
  layerID!: number;
  buildingPlan!: string;
  buildingPlanName!: string;
  sensors?: any[];
  delete?: boolean;
  structureFloorZone?: boolean;
  structureSectionCrossing?: boolean;
  structureAssociatedEquipment?: boolean;
  displayOrder?: number;
  customerName!: string;
  sensorDigitIDs!: string;
  edgeDeviceIDs!: string;
  macs!: string;
  gatewayIDs!: string;
  defaultImageId?: string;

  constructor(
    obj: IOTObject,
    public breadcrumbs: any[]
  ) {
    //super();
    Object.assign(this, obj);
  }
}

export class AssetDetail extends Asset {
  static emMode = false;

  public health!: any[];
  public steamTrap!: any[];
  public notifications!: any[];
  public temperature!: any;
  public additionalInfo!: any[];
  public additionalDocuments!: any[];

  constructor(obj: IOTObject, breadcrumbs: any[]) {
    super(obj, breadcrumbs);
    Object.assign(this, obj);

    if (AssetDetail.emMode) {
      // telemetry status override (set telemetry status according to notifications)
      if (this.sensors?.length) {
        this.sensors = this.sensors.map((sensor: any) => {
          sensor.telemetry = this.telemetryStatusOverride(sensor.telemetry);
          return sensor;
        });
      }
      if (this.health?.length) {
        this.health = this.telemetryStatusOverride(this.health);
      }
      if (this.temperature) {
        this.temperature = this.telemetryStatusOverride([this.temperature])[0];
      }
      if (this.sensors?.length) {
        this.critical = !!this.sensors.find((sensor: any) => sensor.Critical);
        this.warning = !!this.sensors.find((sensor: any) => sensor.Warning) && !this.critical;
      }
    }
  }

  public static FromServiceResponse(response: any): AssetDetail {
    const assetData = response.asset;
    if (!assetData.isAsset) console.warn('WARNING: trying to parse asset from non-asset object.');

    if (AssetDetail.emMode) {
      return Object.assign(
        new AssetDetail(
          {
            objectID: assetData.objectID,
            objectName: assetData.objectName,
            objectTypeID: assetData.objectTypeID,
            objectType: assetData.objectType,
            parentID: assetData.parent?.objectID || 0,
            objectStatus: assetData.objectStatus,
            description: assetData.description,
            colorCode: assetData.colorCode,
            isAsset: true,
            isConnected: assetData.isConnected,
            isLifetimeConnected: assetData.isLifetimeConnected,
            warning: assetData.warning,
            critical: assetData.critical,
            inactive: assetData.inactive,
            isArchived: assetData.isArchived,
            equipmentType: assetData.equipmentType,
            childCount: 0,
            geoPosition: assetData.geoPosition,
            children: [],
            picture: assetData.picture,
            thumbnail: assetData.thumbnail,
            curLayer: assetData.curLayer,
            nextLayer: assetData.nextLayer,
            layerName: assetData.layerName,
            layerID: assetData.layerID,
            buildingPlan: assetData.buildingPlan,
            buildingPlanName: assetData.buildingPlanName,
            numSensors: assetData.numSensors,
            customerName: assetData.customerName,
            sensorDigitIDs: assetData.sensorDigitIDs,
            edgeDeviceIDs: assetData.edgeDeviceIDs,
            macs: assetData.macs,
            gatewayIDs: assetData.gatewayIDs,
            defaultImageId: assetData.defaultImageId,
          },
          [] // breadcrumbs are added later in assets service
        ),
        {
          thumbnail: null,
          sensors: [],
          health: [],
          temperature: null,
          steamTrap: [],
          notifications: (response.notifications || assetData.notifications || []).map(
            (item: any) => new Notification(item, [])
          ),
          additionalInfo: response.additionalInfo || assetData.additionalInfo || [],
          additionalDocuments: response.additionalDocuments || assetData.additionalDocuments || [],
        }
      );
    }

    // IA mode
    const health = ParseHealthFromRawSensorTelemetryIA(response.telemetry);

    return Object.assign(
      new AssetDetail(
        {
          objectID: assetData.objectID,
          objectName: assetData.objectName,
          objectTypeID: assetData.objectTypeID,
          objectType: assetData.objectType,
          parentID: assetData.parent?.objectID || 0,
          objectStatus: assetData.objectStatus,
          description: assetData.description,
          colorCode: assetData.colorCode,
          isAsset: true,
          isConnected: assetData.isConnected,
          warning: assetData.warning,
          critical: assetData.critical,
          equipmentType: assetData.equipmentType,
          equipmentTypeID: assetData.equipmentTypeID,
          childCount: 0,
          geoPosition: assetData.geoPosition,
          children: [],
          picture: assetData.picture,
          thumbnail: assetData.thumbnail,
          curLayer: assetData.curLayer,
          nextLayer: assetData.nextLayer,
          layerName: assetData.layerName,
          layerID: assetData.layerID,
          buildingPlan: assetData.buildingPlan,
          buildingPlanName: assetData.buildingPlanName,
          customerName: assetData.customerName,
          sensorDigitIDs: assetData.sensorDigitIDs,
          edgeDeviceIDs: assetData.edgeDeviceIDs,
          macs: assetData.macs,
          gatewayIDs: assetData.gatewayIDs,
        },
        [] // breadcrumbs are added later in assets service
      ),
      {
        thumbnail: null,
        health: health,
      }
    );
  }

  telemetryStatusOverride(items: any[]): any[] {
    if (!items || !this.notifications?.length) return items || [];

    items.forEach((telemetry: any) => {
      const sensorID = telemetry.sensor?.sensorID;
      const mtID = telemetry.measurementTypeID;

      const matched = this.notifications.filter((n: any) => {
        if (n.sensorID !== sensorID) return false;

        // Inactive leakage also matches direct-C leakage notifications
        if (mtID === INACTIVE_LEAKAGE_ID) {
          return n.measurementTypeID === mtID || n.measurementTypeID === DIRECT_C_LEAKAGE_ID;
        }
        // Inactive steam trap also matches steam trap status notifications
        if (mtID === INACTIVE_STEAM_TRAP_ID || mtID === INACTIVE_STEAM_TRAP_ANALYTICS_ID) {
          return n.measurementTypeID === mtID || n.measurementTypeID === STEAM_TRAP_STATUS_ID;
        }

        return n.measurementTypeID === mtID;
      });

      if (matched.length > 0) {
        telemetry.notifications = matched;
      }
    });

    return items;
  }

  get SensorTileTelemetry(): {
    title: string;
    icon: string;
    telemetry: any[];
  }[] {
    const sensors = this.sensors as any[];
    if (!sensors?.length) return [];

    const allTelemetry: any[] = [];

    // Collect all sensor telemetry with sensor reference
    sensors.forEach((sensor: any) => {
      if (sensor.telemetry) {
        sensor.telemetry.forEach((t: any) => {
          t.sensor = sensor;
          allTelemetry.push(t);
        });
      }
    });

    // Filter out direct-C leakage (shown as part of minmax pair)
    const filtered = allTelemetry.filter((t: any) => t.measurementTypeID !== DIRECT_C_LEAKAGE_ID);

    // Add steam trap telemetry
    if (this.steamTrap?.length > 0) {
      this.steamTrap.forEach((t: any) => {
        if (!t.sensor) {
          t.sensor = sensors[0];
        }
        filtered.push(t);
      });
    }

    // Override notification status
    this.telemetryStatusOverride(filtered);

    // Group by title
    const groups: Record<string, any[]> = {};
    filtered.forEach((t: any) => {
      const title = t.title || '';
      if (!groups[title]) groups[title] = [];
      groups[title].push(t);
    });

    return Object.entries(groups).map(([title, telemetry]) => ({
      title,
      icon: telemetry[0].svg || '',
      telemetry,
    }));
  }

  get SensorTileTelemetryEM() {
    if (!this.sensors || this.sensors.length === 0) return [];
    let telemetry: any[] = this.sensors.map((sensor: any) => sensor.telemetry).flat(2);
    if (this.steamTrap.length > 0) {
      // special case - steam traps:
      if (telemetry.find((item: CurrentTelemetry) => item.measurementTypeID === 1068)) {
        const temperature = telemetry.filter(
          (item: CurrentTelemetry) => item.measurementTypeID === 1073
        );
        telemetry = telemetry
          .filter((item: CurrentTelemetry) => item.measurementTypeID === 1068)
          .map((item) =>
            Object.assign(new TypedSensorTelemetry<number, string>(item.sensor), item)
          );
        if (!!temperature && temperature.length > 0)
          telemetry.map((item: CurrentTelemetry) => {
            item['value'] = JSON.parse(JSON.stringify(item['value']));
            item['value'].timestamp = temperature[0]['value'].timestamp;
          });
      } else {
        // use inlet temp instead, if no analytics data available
        // outlet temp will be added to tile in AssetOverviewComponent
        telemetry = telemetry.filter((item: CurrentTelemetry) => item.measurementTypeID === 1073);
      }
      /* .find(
          (item: CurrentTelemetry) =>
            item.measurementTypeID === 1073 || item.measurementTypeID === 1074
        )*/
    } else if (telemetry.find((item: CurrentTelemetry) => item.measurementTypeID === 1048)) {
      // special case - direct c, leakage
      telemetry = telemetry.filter((item: CurrentTelemetry) => item.measurementTypeID === 1048);
    } else if (
      telemetry.find((item: CurrentTelemetry) => item.measurementTypeID === INACTIVE_LEAKAGE_ID)
    ) {
      // special case - direct c, leakage, if no data are received
      telemetry.filter((item: CurrentTelemetry) => item.measurementTypeID === INACTIVE_LEAKAGE_ID);
    }
    return groupBy(telemetry, 'title').map((_telemetry: any) => {
      return {
        title: _telemetry[0].title,
        icon: _telemetry[0].svg,
        telemetry: _telemetry,
      };
    });
  }

  getSensorDetailTelemetry(sensorId: number): { title: string; icon: string; telemetry: any[] }[] {
    const sensors = this.sensors as any[];
    if (!sensors?.length) return [];

    const sensor = sensors.find((s: any) => s.sensorID === sensorId);
    if (!sensor?.telemetry) return [];

    const allTelemetry: any[] = [];

    sensor.telemetry.forEach((t: any) => {
      t.sensor = sensor;
      allTelemetry.push(t);
    });

    // Filter out direct-C leakage
    const filtered = allTelemetry.filter((t: any) => t.measurementTypeID !== DIRECT_C_LEAKAGE_ID);

    // Add steam trap telemetry for this sensor and propagate status
    if (this.steamTrap?.length > 0) {
      const sensorSteamTrap = this.steamTrap.filter((t: any) => t.sensor?.sensorID === sensorId);

      // Propagate steam trap status to sensor telemetry
      const status = sensorSteamTrap.find((t: any) => t.measurementTypeID === STEAM_TRAP_STATUS_ID);
      if (status) {
        filtered.forEach((t: any) => {
          t.critical = status.critical;
          t.warning = status.warning;
        });
      }

      sensorSteamTrap.forEach((t: any) => {
        if (!t.sensor) t.sensor = sensor;
        filtered.push(t);
      });
    }

    // Override notification status
    this.telemetryStatusOverride(filtered);

    // Group by title
    const groups: Record<string, any[]> = {};
    filtered.forEach((t: any) => {
      const title = t.title || '';
      if (!groups[title]) groups[title] = [];
      groups[title].push(t);
    });

    return Object.entries(groups).map(([title, telemetry]) => ({
      title,
      icon: telemetry[0].svg || '',
      telemetry,
    }));
  }

  getSensorDetailTelemetryEM(sensorid: number) {
    if (!this.sensors || this.sensors.length === 0) return [];
    let telemetry: CurrentTelemetry[] | any = this.sensors
      .filter((sensor) => sensor.sensorID === sensorid)
      .map((sensor: Sensor) => sensor.telemetry)
      .flat(2);
    if (this.steamTrap.length > 0) {
      // special case - steam traps:
      const status = telemetry.find((item: any) => item.measurementTypeID === STEAM_TRAP_STATUS_ID);
      if (status) {
        telemetry.map((item: any) => {
          if (item.measurementTypeID !== STEAM_TRAP_STATUS_ID) {
            item.critical = status.critical;
            item.warning = status.warning;
          }
        });
      }
      telemetry = telemetry.filter(
        (item: CurrentTelemetry) => item.measurementTypeID === 1073 // || item.measurementTypeID === 1074
      );
    } else if (telemetry.find((item: CurrentTelemetry) => item.measurementTypeID === 1048)) {
      // special case - direct c, leakage
      telemetry = telemetry.filter((item: CurrentTelemetry) => item.measurementTypeID === 1048);
    } else if (telemetry.find((item: CurrentTelemetry) => item.measurementTypeID === 1026)) {
      // special case - direct c, leakage
      telemetry = telemetry.filter((item: CurrentTelemetry) => item.measurementTypeID === 1026);
    }

    return groupBy(telemetry, 'title').map((_telemetry: any) => {
      return {
        title: _telemetry[0].title,
        icon: _telemetry[0].svg,
        telemetry: _telemetry,
      };
    });
  }

  // public ApplyMeasurementSystem(
  //   measurementSystem: MeasurementSystemSettings,
  // ): void {
  //   this.sensors = this.sensors.map((sensor) => {
  //     sensor.telemetry = sensor.telemetry.map((telemetry: CurrentTelemetry) => {
  //       telemetry.ApplyMeasurementSystem(measurementSystem);
  //       return telemetry;
  //     });
  //     return sensor;
  //   });
  //   if (!!this.temperature) {
  //     this.temperature.ApplyMeasurementSystem(measurementSystem);
  //   }
  //   this.health = this.health.map((telemetry: CurrentTelemetry) => {
  //     telemetry.ApplyMeasurementSystem(measurementSystem);
  //     return telemetry;
  //   });
  //   this.steamTrap = this.steamTrap.map((telemetry: CurrentTelemetry) => {
  //     telemetry.ApplyMeasurementSystem(measurementSystem);
  //     return telemetry;
  //   });
  // }
}
